import {
  cva as cvaOriginal,
  type VariantProps as VariantPropsOriginal,
} from "class-variance-authority";
import {
  type ClassProp,
  type ClassValue,
  type StringToBoolean,
} from "class-variance-authority/types";
import { omit } from "lodash-es";
import { cn } from "./cn";

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- original generic
export type VariantProps<Component extends (...args: any) => any> =
  VariantPropsOriginal<Component>;

type ConfigSchema = Record<string, Record<string, ClassValue>>;
type ConfigVariants<T extends ConfigSchema> = {
  [Variant in keyof T]?: StringToBoolean<keyof T[Variant]> | null | undefined;
};
type ConfigVariantsMulti<T extends ConfigSchema> = {
  [Variant in keyof T]?:
    | StringToBoolean<keyof T[Variant]>
    | StringToBoolean<keyof T[Variant]>[]
    | undefined;
};
type Config<T> = T extends ConfigSchema
  ? {
      variants?: T;
      defaultVariants?: ConfigVariants<T>;
      compoundVariants?: (T extends ConfigSchema
        ? (ConfigVariants<T> | ConfigVariantsMulti<T>) & ClassProp
        : ClassProp)[];
    }
  : never;

export function cva<T>(
  styles: string,
  config: Config<T> | undefined,
): {
  variants: ReturnType<typeof cvaOriginal<T>>;
  makeProps: <P extends { className?: string }>(
    props: P,
  ) => Omit<P, keyof Exclude<Config<T>["variants"], undefined>> & {
    className: string;
  };
} {
  const variants = cvaOriginal<T>(styles, config);
  const variantKeys = Object.keys(config?.variants || {});

  function makeProps<P extends { className?: string }>(
    props: P,
  ): Omit<P, keyof Exclude<Config<T>["variants"], undefined>> & {
    className: string;
  } {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- too complex
    const className = cn(variants(props as any), props.className);

    return {
      ...(omit(props, variantKeys) as Omit<
        P,
        keyof Exclude<Config<T>["variants"], undefined>
      > & {
        className: string;
      }),
      className,
    };
  }

  return {
    variants,
    makeProps,
  };
}
